import { useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";

import { AutoComplete, Input } from "../../components/UI/Layout";
import { useAppSelector, useAppDispatch } from "../../hooks";
import type { ICartItem, IProductOption } from "../../store/types";
import { addOrderItem } from "../../store/order";
import { setSiderCollapsed, setSiderMode } from "../../store/sider";
import { clearProducts, fetchProducts } from "../../store/productSearch";
import { productOptions } from "../../store/selector/product";
import { genHash, itemsSelector } from "../../store/selector/cart";
import { categoriesPathSelector } from "../../store/selector/category";

export const ProductInput = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [value, setValue] = useState("");

  const { categoriesPathMap, items, options, products, loading } = useAppSelector((state) => {
    return {
      categoriesPathMap: categoriesPathSelector(state),
      items: itemsSelector(state),
      options: productOptions(state),
      products: state.productSearch.list,
      loading: state.customer.loading,
    };
  });
  const onSearch = (searchText: string) => {
    if (!searchText) {
      clearProducts()(dispatch);
    } else {
      fetchProducts(searchText)(dispatch);
    }
  };

  const onSelect = (data: string) => {
    const product = products.find((o: any) => o._id === data);
    if (product && product?.category) {
      const path = "/" + (categoriesPathMap.get(product.category) || "");
      const newElement = { ...product, quantity: 1, url: path };

      if (!product.subcategories?.length) {
        const existItem = items.find((o: ICartItem) => o._id === product._id);
        newElement.hash = existItem?.hash || genHash();
      } else {
        newElement.hash = genHash();
      }

      const { hash, subcategories: categoryModifiers = [] } = newElement;
      const parent = path.split("/p/")[0];

      if (categoryModifiers && categoryModifiers.length) {
        navigate(`${parent}/p/${hash}/m`);
      } else {
        let url = parent.includes("/p") ? `${parent}` : `${parent}/p`
        newElement.url = url;
        navigate(`${url || ""}`);
      }
      addOrderItem(newElement)(dispatch);
      setSiderCollapsed(false)(dispatch);
      setSiderMode("view")(dispatch);
    }
    setValue("");
  };

  const onChange = (data: string) => {
    setValue(data);
  };

  const optionsNotFound = [
    {
      value: "Not Found",
      label: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Not Found
        </div>
      ),
    },
  ];

  const renderItem = ({
    label,
    value,
    details,
  }: {
    label: string;
    value: string;
    details?: string;
  }) => ({
    value: value,
    label: (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={label}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "inline-block",
            maxWidth: "calc(100% - 60px)",
          }}
        >
          {label}
        </span>
        {details ? <span>{details}</span> : null}
      </div>
    ),
  });

  const dropdownItems = (
    options: { label: string; value: string; details?: string }[]
  ) => options.map(renderItem);

  return (
    <div className="mb15" style={{ zIndex: 0, position: "relative", width: "100%", minWidth: "245px", maxWidth: "600px" }}>
      <AutoComplete
        value={value}
        allowClear={true}
        onSelect={onSelect}
        onSearch={onSearch}
        onChange={onChange}
        popupClassName="z-index0"
        notFoundContent={loading ? "Loading" : "Not Found"}
        options={
          !options.length && value ? optionsNotFound : dropdownItems(options)
        }
        style={{ width: "100%", verticalAlign: "middle" }}
      >
        <Input
          prefix={<SearchOutlined />}
          placeholder=" Product"
        />
      </AutoComplete>
    </div>
  );
};
